import {
  first_value,
  read_safe_object_value,
  write_safe_object_value
} from '@/services/onboarding_form_helpers';

export function normalize_answer_group_key(value) {
  const normalized_value = String(value ?? '').trim();
  return normalized_value || null;
}

function get_question_trigger_condition(question = {}) {
  const trigger_condition = String(question?.trigger_condition || '').trim().toLowerCase();
  return trigger_condition;
}

function normalize_question_answer_values(value) {
  if (Array.isArray(value)) {
    const normalized_values = value.map((item) => String(item ?? '').trim().toLowerCase()).filter(Boolean);
    return normalized_values;
  }
  const normalized = String(value ?? '').trim().toLowerCase();
  return normalized ? [normalized] : [];
}

function question_trigger_matches(question = {}, answer_value) {
  const trigger_condition = get_question_trigger_condition(question);
  if (!trigger_condition) return true;
  return normalize_question_answer_values(answer_value).includes(trigger_condition);
}

function collect_visible_questions(questions = [], resolve_question_value, results = []) {
  questions.forEach((question) => {
    results.push(question);
    const dependent_questions = Array.isArray(question?.dependent_questions) ? question.dependent_questions : [];
    if (!dependent_questions.length) return;

    const answer_value = resolve_question_value(question);
    dependent_questions.forEach((dependent_question) => {
      if (!question_trigger_matches(dependent_question, answer_value)) return;
      collect_visible_questions([dependent_question], resolve_question_value, results);
    });
  });

  return results;
}

export function get_visible_questions(questions = [], form_values = {}) {
  const visible_questions = collect_visible_questions(
    questions,
    (question) => read_safe_object_value(form_values, question.id, question.answer ?? ''),
    []
  );
  return visible_questions;
}

export function resolve_section_question_id(question = {}) {
  const section_question_id = Number(first_value([question?.section_question_id, question?.id], 0)) || null;
  return section_question_id;
}

export function build_saved_answers_maps(extracted_saved_answers) {
  const by_question_id = extracted_saved_answers.reduce((map, answer) => {
    const existing_answers = map.get(answer.section_question_id) || [];
    existing_answers.push(answer);
    map.set(answer.section_question_id, existing_answers);
    return map;
  }, new Map());
  const by_question_label = new Map(
    extracted_saved_answers.reduce((entries, answer) => {
      if (!answer.question_label) return entries;
      const existing_answers = entries.get(answer.question_label) || [];
      existing_answers.push(answer);
      entries.set(answer.question_label, existing_answers);
      return entries;
    }, new Map())
  );
  const saved_answer_maps = {
    byQuestionId: by_question_id,
    byQuestionLabel: by_question_label
  };
  return saved_answer_maps;
}

function normalize_multi_select_answer(saved_answer) {
  const normalized_multi_select_answer = String(saved_answer.answer_text || '')
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean);
  return normalized_multi_select_answer;
}

function build_file_from_saved_answer(saved_answer = {}) {
  return {
    id: saved_answer.id,
    file_name: saved_answer.file_name || saved_answer.answer_text || 'Uploaded file',
    presigned_url: saved_answer.file_url,
    original_url: saved_answer.file_url
  };
}

export function aggregate_file_saved_answers(saved_answers = []) {
  const first_answer = saved_answers[0] || null;
  if (!first_answer) return null;

  const files = saved_answers.flatMap((saved_answer) => (
    Array.isArray(saved_answer.files) && saved_answer.files.length
      ? saved_answer.files
      : saved_answer.file_url
        ? [build_file_from_saved_answer(saved_answer)]
        : []
  ));

  return {
    ...first_answer,
    files
  };
}

function apply_file_answer_to_question({ question, saved_answer, persisted_file_urls }) {
  const saved_files = Array.isArray(saved_answer.files) && saved_answer.files.length
    ? saved_answer.files
    : saved_answer.file_url
      ? [{
          id: saved_answer.id,
          file_name: saved_answer.file_name || saved_answer.answer_text || 'Uploaded file',
          presigned_url: saved_answer.file_url,
          original_url: saved_answer.file_url
        }]
      : [];

  question.answer = saved_files.length
    ? { files: saved_files }
    : saved_answer.file_name || saved_answer.answer_text || '';
  if (!saved_files.length) return;

  write_safe_object_value(
    persisted_file_urls,
    question.id,
    saved_files.map((file) => {
      const saved_file = {
        id: file.id,
        name: first_value([file.file_name, file.name], 'Uploaded file'),
        url: first_value([file.presigned_url, file.url, file.original_url], '')
      };
      return saved_file;
    }).filter((file) => file.url)
  );
}

export function apply_saved_answer_to_question({ question, saved_answer, persisted_file_urls }) {
  if (!saved_answer) return;
  if (question.type === 'multi_select') {
    question.answer = normalize_multi_select_answer(saved_answer);
    return;
  }
  if (question.type === 'file') {
    apply_file_answer_to_question({ question, saved_answer, persisted_file_urls });
    return;
  }
  question.answer = saved_answer.answer_text;
}

function build_persisted_file_value(question, persisted_file_urls) {
  const persisted_file = read_safe_object_value(persisted_file_urls, question.id, null);
  const answer_files = Array.isArray(question.answer?.files)
    ? question.answer.files
    : question.answer && typeof question.answer === 'object'
      ? [question.answer]
      : [];
  const persisted_files = Array.isArray(persisted_file)
    ? persisted_file
    : persisted_file
      ? [persisted_file]
      : answer_files;

  const persisted_file_value = persisted_files
    .map((file) => {
      const persisted_entry = {
        id: file.id,
        name: first_value([file.file_name, file.name], 'Uploaded file'),
        persisted: true,
        url: first_value([
          file.presigned_url,
          file.url,
          file.original_url
        ], '')
      };
      return persisted_entry;
    })
    .filter((file) => file.url);
  return persisted_file_value;
}

export function prefill_question_value({ question, form_values, file_values, persisted_file_urls }) {
  if (!question.id) return;

  if (question.type === 'multi_select') {
    write_safe_object_value(form_values, question.id, Array.isArray(question.answer) ? question.answer : []);
  } else if (question.type === 'file') {
    write_safe_object_value(file_values, question.id, build_persisted_file_value(question, persisted_file_urls));
  } else {
    write_safe_object_value(form_values, question.id, question.answer ?? '');
  }

  (Array.isArray(question?.dependent_questions) ? question.dependent_questions : []).forEach((dependent_question) => {
    prefill_question_value({
      question: dependent_question,
      form_values,
      file_values,
      persisted_file_urls
    });
  });
}
